/**
 * BUILD-M1 — Team directory (org users).
 *
 * Read-only list of everyone in the organization (name + role + active status), sourced from
 * `listOrgUsers` (GET /api/org/users). Distinct from Users & roles (owner-only management): this is a
 * reachable directory for name display, reached from the Account hub.
 *
 * NEVER imports @smartbox/* (mobile invariant 1). Design-system primitives only; no raw styles.
 */
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { listOrgUsers, type OrgUser } from '../../../../src/api/assistant';
import { Screen, Text, Card, Button, ListRow, Badge } from '../../../../src/components/ui';
import type { StatusKey } from '../../../../src/theme';
import { theme } from '../../../../src/theme';
import { extractErrorMessage, humanize } from '../../../../src/lib/format';

export default function TeamScreen() {
  const usersQuery = useQuery({ queryKey: ['org-users'], queryFn: listOrgUsers, staleTime: 60_000 });
  const users = usersQuery.data ?? [];

  return (
    <Screen scroll>
      <Text variant="title">Team</Text>
      <Text variant="body" color="secondary">
        Everyone in your organization. Manage roles under Users & roles.
      </Text>

      {usersQuery.isLoading ? (
        <ActivityIndicator color={theme.color.action.primary} accessibilityLabel="Loading team" />
      ) : usersQuery.isError ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {extractErrorMessage(usersQuery.error, 'Failed to load your team.')}
          </Text>
          <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => usersQuery.refetch()} />
        </Card>
      ) : users.length === 0 ? (
        <Card>
          <Text variant="body" color="secondary">No teammates yet.</Text>
        </Card>
      ) : (
        <View style={styles.list}>
          {users.map((u: OrgUser) => {
            const b: { key: StatusKey; label: string } = u.isActive
              ? { key: 'paid', label: 'Active' }
              : { key: 'draft', label: 'Inactive' };
            return (
              <ListRow
                key={u.id}
                title={u.fullName}
                subtitle={humanize(u.role)}
                right={<Badge status={b.key} label={b.label} />}
              />
            );
          })}
        </View>
      )}
    </Screen>
  );
}

const styles = StyleSheet.create({
  list: { gap: theme.spacing.sm },
});
